import type {
  KeyInsight,
  MerchantKeyInsightsResponse,
  MerchantRedemptionsTrendResponse,
  MerchantTopCouponsResponse,
  MerchantTrendChartPoint,
  TopCoupon,
} from "../types/merchant-home-data.types";

export const FALLBACK_KEY_INSIGHTS: KeyInsight[] = [
  {
    id: "1",
    title: "Your redemption rate is 15% higher than similar merchants",
    icon_type: "trend",
  },
  { id: "2", title: "Most redemptions happen between 2-5pm", icon_type: "clock" },
  { id: "3", title: "Your redemptions peaked on Saturdays", icon_type: "calendar" },
];

export const FALLBACK_TOP_COUPONS: TopCoupon[] = [
  { id: "1", title: "20% Off Pizza", redemptions: 342, growth: 15 },
  { id: "2", title: "Buy 1 Get 1 Free Drink", redemptions: 287, growth: 8 },
  { id: "3", title: "$5 Off $25 Purchase", redemptions: 198, growth: -3 },
];

export const getFormattedDate = (): string => {
  const now = new Date();
  const dayOfWeek = now.toLocaleDateString("en-US", { weekday: "long" });
  const month = now.toLocaleDateString("en-US", { month: "long" });
  const day = now.getDate();

  const getOrdinalSuffix = (dayOfMonth: number): string => {
    if (dayOfMonth > 3 && dayOfMonth < 21) return "th";
    switch (dayOfMonth % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  return `${dayOfWeek}, ${month} ${day}${getOrdinalSuffix(day)}`;
};

export const resolveExpiringSoonCount = (expiringSoon: number | undefined): number => {
  if (!expiringSoon || expiringSoon === 0) {
    return 3;
  }

  return expiringSoon;
};

export const mapKeyInsightsToUI = (insights: MerchantKeyInsightsResponse): KeyInsight[] => {
  const result: KeyInsight[] = [];

  if (
    insights.redemption_rate_comparison?.message &&
    !insights.redemption_rate_comparison.message.includes("Not enough data")
  ) {
    const { percentage_difference, is_higher } = insights.redemption_rate_comparison;
    const absDiff = Math.abs(percentage_difference);
    result.push({
      id: "redemption_rate_comparison",
      title: `Your redemption rate is ${absDiff}% ${is_higher ? "higher" : "lower"} than similar merchants`,
      icon_type: "trend",
    });
  }

  if (
    insights.peak_redemption_time?.message &&
    !insights.peak_redemption_time.message.includes("Not enough")
  ) {
    const { start_hour, end_hour } = insights.peak_redemption_time;
    const formatHour = (hour: number) => {
      if (hour === 0) return "12am";
      if (hour < 12) return `${hour}am`;
      if (hour === 12) return "12pm";
      return `${hour - 12}pm`;
    };
    result.push({
      id: "peak_redemption_time",
      title: `Most redemptions happen between ${formatHour(start_hour)}-${formatHour(end_hour)}`,
      icon_type: "clock",
    });
  }

  if (
    insights.peak_redemption_day?.message &&
    !insights.peak_redemption_day.message.includes("Not enough")
  ) {
    result.push({
      id: "peak_redemption_day",
      title: `Your redemptions peaked on ${insights.peak_redemption_day.day_of_week}`,
      icon_type: "calendar",
    });
  }

  return result;
};

export const getDisplayKeyInsights = (
  insights: MerchantKeyInsightsResponse | null,
): KeyInsight[] => {
  const mapped = insights ? mapKeyInsightsToUI(insights) : [];

  return mapped.length > 0 ? mapped : FALLBACK_KEY_INSIGHTS;
};

export const mapTopCouponsToUI = (couponsData: MerchantTopCouponsResponse): TopCoupon[] => {
  if (!couponsData.coupons || !Array.isArray(couponsData.coupons)) {
    return [];
  }

  return couponsData.coupons
    .filter((coupon) => coupon.title)
    .map((coupon, index) => ({
      id: coupon.coupon_id || `coupon-${index}`,
      title: coupon.title || "Untitled Coupon",
      redemptions: coupon.redemptions_this_month ?? 0,
      growth:
        coupon.growth_percentage !== undefined
          ? (coupon.is_growth_positive === false ? -1 : 1) * coupon.growth_percentage
          : 0,
    }));
};

export const getDisplayTopCoupons = (
  couponsData: MerchantTopCouponsResponse | null,
): TopCoupon[] => {
  const mapped = couponsData ? mapTopCouponsToUI(couponsData) : [];

  return mapped.length > 0 ? mapped : FALLBACK_TOP_COUPONS;
};

export const getTrendChartPoints = (
  trend: MerchantRedemptionsTrendResponse | null,
): MerchantTrendChartPoint[] => {
  const chartData = trend?.chart_data ?? [];

  return chartData.map((item) => ({
    week: item?.week_label ?? "",
    value: item?.count ?? 0,
  }));
};
